import './App.css';

import BugsnagPluginReact, {
  BugsnagErrorBoundary
} from '@bugsnag/plugin-react';
import React, { Suspense, useEffect, useMemo } from 'react';
import {
  Redirect,
  Route,
  BrowserRouter as Router,
  Switch,
  useLocation
} from 'react-router-dom';
import {
  SITE_PRIMARY_COLOR,
  SITE_SECONDARY_COLOR
} from '../src/Observodon/constants';
import { UiRoutes, sampleLtiRoute } from './common/UiRoutes';
import { enUS, jaJP } from '@mui/material/locale';

import { AccountContextProvider } from './Observodon/models/AccountContext';
import Bugsnag from '@bugsnag/js';
import Centered from './shared/Centered';
import CircularProgress from '@mui/material/CircularProgress';
import Home from './Host/Home';
import { IdentityRoutes } from './common/IdentityRoutes';
import PrivateRoute from './auth/PrivateRoute';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import config from './config';
import { createTheme } from '@mui/material/styles';
import { loadLanguage } from './i18n';
import { practiceRoute } from './common/PracticeRoutes';
import useThemes from './hooks/useThemes';
import { useTranslation } from 'react-i18next';
import useViewport from './Observodon/hooks/useViewport';

// Authenticated system.
const IdentityNotSupported = React.lazy(
  () => import('./membership/IdentityNotSupported')
);

const authRoutes = [
  IdentityRoutes.REGISTER,
  IdentityRoutes.VERIFY_EMAIL,
  IdentityRoutes.REQUEST_VERIFICATION,
  IdentityRoutes.REQUEST_PASSWORD,
  IdentityRoutes.RESET_PASSWORD,
  IdentityRoutes.INVITATION
];

// Observodon views.
const [Handoff, Rubric, ModerationHub] = [
  React.lazy(() => import('./Observodon/components/Handoff/Handoff')),
  React.lazy(() => import('./Observodon/components/Rubric/ManageRubric')),
  React.lazy(
    () => import('./Observodon/components/ModerationHub/ModerationHub')
  )
];

// Hosting samples.
const [HostSamples, LtiOAuth2, EmbedSample, OpenAccessSample, DeviceIssues] = [
  React.lazy(() => import('./Host/HostSamples')),
  React.lazy(() => import('./Host/HostSamplesLti')),
  React.lazy(() => import('./Host/EmbedSample')),
  React.lazy(() => import('./Host/OpenAccessSample')),
  React.lazy(() => import('./Host/DeviceIssues'))
];

// Practice, admin views.
const [Practice, Admin, Trial, Mfa] = [
  React.lazy(() => import('./Practice/Practice')),
  React.lazy(() => import('./Admin/Admin')),
  React.lazy(() => import('./Trial/Trial')),
  React.lazy(() => import('./Admin/Mfa'))
];

Bugsnag.start({
  apiKey:
    process.env.REACT_APP_BUGSNAG_API_KEY || '00000111112222233333444445555566',
  plugins: [new BugsnagPluginReact()],
  releaseStage: config.ENV,
  enabledReleaseStages: ['dev', 'staging', 'live']
});
const ErrorBoundary = Bugsnag.getPlugin('react')?.createErrorBoundary(
  React
) as BugsnagErrorBoundary;

function getMuiLocale(language: string) {
  switch (language) {
    case 'ja':
      return jaJP;
    default:
      return enUS;
  }
}

const useQueryParams = () => {
  return new URLSearchParams(window.location.search);
};

/**
 * Top-level application component.
 */
function App() {
  useViewport();
  const { muiTheme } = useThemes();
  const { i18n } = useTranslation();

  const query = useQueryParams();
  const locale = query.get('locale') || 'en';

  useEffect(() => {
    const setLanguage = async () => {
      if (locale) {
        await loadLanguage(locale);
        await i18n.changeLanguage(locale);
      }
    };
    setLanguage();
  }, [locale]);

  const fontFamily = muiTheme.typography.fontFamily;
  muiTheme.typography.fontFamily =
    muiTheme.typography.h1.fontFamily =
    muiTheme.typography.h2.fontFamily =
    muiTheme.typography.h3.fontFamily =
    muiTheme.typography.h4.fontFamily =
    muiTheme.typography.h5.fontFamily =
    muiTheme.typography.h6.fontFamily =
    muiTheme.typography.subtitle1.fontFamily =
    muiTheme.typography.subtitle2.fontFamily =
    muiTheme.typography.body1.fontFamily =
    muiTheme.typography.body2.fontFamily =
    muiTheme.typography.caption.fontFamily =
    muiTheme.typography.button.fontFamily =
    muiTheme.typography.overline.fontFamily =
      fontFamily;

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--app-primary',
      SITE_PRIMARY_COLOR
    );
    document.documentElement.style.setProperty(
      '--app-secondary',
      SITE_SECONDARY_COLOR
    );
  }, []);

  const theme = useMemo(() => {
    return createTheme(muiTheme, getMuiLocale(i18n.language));
  }, [i18n.language]);
  return (
    <AccountContextProvider value={{ locale: locale }}>
      <ErrorBoundary>
        <ThemeProvider theme={theme}>
          <Router>
            <Suspense
              fallback={
                <Centered>
                  <CircularProgress size={32} />
                </Centered>
              }
            >
              <Switch>
                <Route exact path="/" component={Home} />

                {/*
                 * Authentication system.
                 */}
                {authRoutes.map((path) => (
                  <Route
                    key={`${path}`}
                    path={path}
                    exact
                    component={IdentityNotSupported}
                  />
                ))}

                {/* Logged-in practice views. */}
                <Route path={practiceRoute} component={Practice} />
                <Route path={UiRoutes.TRIAL} component={Trial} />
                <PrivateRoute path={UiRoutes.ADMIN} component={Admin} />
                <Route path="/mfa" component={Mfa} />

                {/*
                 * Demo LTI 1.3 / OAuth2 launcher to demonstrate how the Viewer
                 * can be included in a host system. This is comprised of:
                 *  - /launch-viewer: Page that hosts the viewer iframe
                 *  - /launch-viewer/:instanceId: Launch page, from within iframe,
                 *    that performs a POST to the /auth/v/launch endpoint with a
                 *    suitable id_token.
                 *  - privatkeys.json: Used by both server and front-end to sign
                 *    the supplied id_token.
                 *  - HostAuthService: A server endpoint that delivers the jwks
                 *    signing keys.
                 */}
                <Route path={sampleLtiRoute} component={LtiOAuth2} />
                <Route path={UiRoutes.SAMPLE_EMBED} component={EmbedSample} />
                <Route
                  path={UiRoutes.SAMPLE_OPEN_ACCESS}
                  component={OpenAccessSample}
                />

                {/* Sample local/OAuth2/hmac/apikey authenticated requests. */}
                <Route path={UiRoutes.SAMPLES} component={HostSamples} />
                <Route path={UiRoutes.SAMPLE_HOME}>
                  <Redirect to={UiRoutes.SAMPLES.replace(':auth', 'oauth2')} />
                </Route>
                <Route
                  path={UiRoutes.SAMPLE_DEVICE_ISSUES}
                  component={DeviceIssues}
                />

                {/* Standalone viewer launched with code. */}
                <Route path={UiRoutes.HANDOFF} component={Handoff} />
                <Route path={UiRoutes.RUBRIC} component={Rubric} />
                <Route
                  path={UiRoutes.MODERATION_HUB}
                  component={ModerationHub}
                />
              </Switch>
            </Suspense>
          </Router>
        </ThemeProvider>
      </ErrorBoundary>
    </AccountContextProvider>
  );
}

export default App;
